import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, ArrowLeft, Loader2, MailCheck } from 'lucide-react';
import { FeatureErrorBoundary } from '@/core/error-boundaries/public';
import { sentryReporter } from '@/core/observability/public';
import { useTranslation } from 'react-i18next';

import { createSignUpSchema, type SignUpFormValues } from '../schemas';
import { useSignUp } from '../hooks/useSignUp';
import { SocialAuthGroup } from '../components/SocialAuthGroup';
import { AuthDivider } from '../components/AuthDivider';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';

import { Button } from '@/shared/components/ui/button';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { FieldGroup } from '@/shared/components/ui/field';
import { PasswordStrengthBar } from '../components/PasswordStrengthBar';

export function RegisterPage() {
  const { t } = useTranslation('auth');
  const { mutate: signUp, isPending, isError, error, needsConfirmation } = useSignUp();
  const [showPassword, setShowPassword] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  const schema = createSignUpSchema(t);

  const form = useForm<SignUpFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', email: '', password: '', confirmPassword: '' },
  });

  // Observamos password para pasarlo al PasswordStrengthBar
  const passwordValue = form.watch('password');

  const onSubmit = (values: SignUpFormValues) => {
    const { confirmPassword: _, ...credentials } = values;
    setSubmittedEmail(values.email);
    signUp(credentials);
  };

  const passwordToggle = (
    <button
      type="button"
      data-testid="auth-register-password-toggle"
      onClick={() => setShowPassword((v) => !v)}
      aria-label={showPassword ? t('aria.hide_password') : t('aria.show_password')}
      className="text-muted-foreground hover:text-foreground transition-colors"
    >
      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
    </button>
  );

  const confirmToggle = (
    <button
      type="button"
      data-testid="auth-register-confirm-password-toggle"
      onClick={() => setShowConfirm((v) => !v)}
      aria-label={showConfirm ? t('aria.hide_password') : t('aria.show_password')}
      className="text-muted-foreground hover:text-foreground transition-colors"
    >
      {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
    </button>
  );

  if (needsConfirmation) {
    return (
      <Card className="w-full border-0 shadow-none bg-transparent">
        <CardHeader className="text-center space-y-2">
          <div className="flex justify-center">
            <MailCheck className="h-10 w-10 text-primary" aria-hidden="true" />
          </div>
          <CardTitle className="text-2xl font-bold">{t('register.check_email_title')}</CardTitle>
          <CardDescription>
            {t('register.check_email_desc', { email: submittedEmail })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Link to="/login">
            <Button variant="outline" className="w-full">
              <ArrowLeft className="w-4 h-4" />
              {t('register.back_to_login')}
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <FeatureErrorBoundary reporter={sentryReporter}>
      <Card className="w-full border-0 shadow-none bg-transparent">
        <CardHeader className="text-center space-y-1">
          <CardTitle className="text-2xl font-bold">{t('register.title')}</CardTitle>
          <CardDescription>{t('register.subtitle')}</CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <SocialAuthGroup />

          <AuthDivider label={t('register.divider')} />

          <form id="register-form" onSubmit={form.handleSubmit(onSubmit)}>
            <FieldGroup>
              {isError && (
                <Alert variant="destructive">
                  <AlertDescription>{error?.message ?? t('errors.generic')}</AlertDescription>
                </Alert>
              )}

              <FormFieldInput
                control={form.control}
                name="name"
                label={t('register.name')}
                type="text"
                autoComplete="name"
                disabled={isPending}
                floatingLabel
                floatingVariant="default"
              />

              <FormFieldInput
                control={form.control}
                name="email"
                label={t('register.email')}
                type="email"
                autoComplete="email"
                disabled={isPending}
                floatingLabel
                floatingVariant="default"
              />

              {/* Password con strength bar debajo */}
              <div className="flex flex-col gap-1.5">
                <FormFieldInput
                  control={form.control}
                  name="password"
                  label={t('register.password')}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  disabled={isPending}
                  floatingLabel
                  floatingVariant="default"
                  endElement={passwordToggle}
                />
                <PasswordStrengthBar password={passwordValue} />
              </div>

              <FormFieldInput
                control={form.control}
                name="confirmPassword"
                label={t('register.confirm_password')}
                type={showConfirm ? 'text' : 'password'}
                autoComplete="new-password"
                disabled={isPending}
                floatingLabel
                floatingVariant="default"
                endElement={confirmToggle}
              />
            </FieldGroup>
          </form>

          {/* Términos */}
          <p className="text-xs text-muted-foreground text-center leading-relaxed">
            {t('register.terms')}{' '}
            <Link to="/terms" className="text-primary hover:underline underline-offset-2">
              {t('register.terms_link')}
            </Link>{' '}
            {t('register.privacy')}{' '}
            <Link to="/privacy" className="text-primary hover:underline underline-offset-2">
              {t('register.privacy_link')}
            </Link>
            .
          </p>

          {/* Submit */}
          <Button
            type="submit"
            form="register-form"
            className="w-full"
            disabled={isPending}
            variant="default"
            aria-busy={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                {t('register.submitting')}
              </>
            ) : (
              <>
                {t('register.submit')}
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </Button>

          <p className="text-sm text-muted-foreground text-center">
            {t('register.has_account')}{' '}
            <Link
              to="/login"
              className="text-primary hover:underline underline-offset-2 transition-colors"
            >
              {t('register.login_link')}
            </Link>
          </p>
        </CardContent>
      </Card>
    </FeatureErrorBoundary>
  );
}
